'use client'

// imports
import { myListShows } from '@/lib/resources/my-list-shows';
import { usePersonalizedResource } from '@/lib/hooks/usePersonalizedResource';
import { myListVideos } from '@/lib/resources/my-list-videos';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';

// lib files
import {
  addFavoriteShow,
  addFavoriteVideo,
  removeFavoriteShow,
  removeFavoriteVideo
} from '@/lib/helpers/favorites';
import {
  PersonalFavoriteShowsContent,
  PersonalFavoriteVideosContent,
  ProfileData,
} from '@/lib/types/api/profile-data';
import { sendPbsGtmEvent, EventData } from "@/lib/helpers/send-pbs-gtm-event";
import { ShowRowContent, ShowDetails } from '@/lib/types/api/show-data';
import { TMP_VIDEO_COOKIE, TMP_SHOW_COOKIE } from '@/lib/constants';
import { userProfile } from '@/lib/atoms/profile';
import { VideoClass } from '@/lib/types/api/video';

// components
import MyListButtonProps from '@/components/Button/MyList/MyListTypes';

// svg's
import PlusIcon from '@/public/svg/add.svg';
import MinusIcon from '@/public/svg/minus.svg';
import CheckIcon from '@/public/svg/check.svg';

// styles
import styles from './MyListButton.module.scss';

const baseEventData: EventData = {
  feature_category: "my list",
  feature_name: "my list button",
  object_action: "click",
  object_type: "button",
  signed_in_status: true,
}

const SignedInMyListButton = (props: MyListButtonProps) => {
  const { video, show, style, depIsInList } = props;
  const [ myListShowsData, setMyListShowsData ] = useAtom(myListShows.itemsAtom);
  const [ myListVideosData, setMyListVideosData ] = useAtom(myListVideos.itemsAtom);
  const [ isInList, setIsInList ] = useState(depIsInList ?? false);
  const [ profile, setProfile ] = useAtom(userProfile);

  // isSettled rather than a render status: this needs "has the list finished
  // loading", which is false while idle as well as while loading.
  const showsLoaded = usePersonalizedResource(myListShows).isSettled;
  const videosLoaded = usePersonalizedResource(myListVideos).isSettled;

  const myListIsLoaded =
    depIsInList !== undefined ||
    (
      // If only a show is provided, we only need the shows list ready
      (show && !video && showsLoaded) ||
      // If only a video is provided, we only need the videos list ready
      (video && !show && videosLoaded) ||
      // If both are provided, maintain existing behavior: require both lists
      (show && video && showsLoaded && videosLoaded) ||
      // Fallback: if neither show nor video is provided, require both as before
      (!show && !video && showsLoaded && videosLoaded)
    )

  // This massive useEffect handles a few things on load of the button:
  // if a temp cookie is present for the show/video, add it to the user's list.
  // Else, if the video/show is already in the user's list, set isInList to true.
  useEffect(() => {
    if (depIsInList === undefined && show && myListIsLoaded) {
      const shows = myListShowsData ?? [];
      const alreadyInList = shows.some(s => s.slug === show.slug);
      const tmpShowCid = Cookies.get(TMP_SHOW_COOKIE);
      if (!alreadyInList && tmpShowCid && show.cid === tmpShowCid) {
        setIsInList(true);
        setMyListShowsData([show, ...shows]);
        (async () => {
          try {
            await addFavoriteShow(show.cid);
            Cookies.remove(TMP_SHOW_COOKIE);
            const eventData: EventData = {
              ...baseEventData,
              franchise_title: show.franchise?.title,
              object_action_behavior: "adds show to list after sign in",
              object_name: "my list - add show to list",
              pbs_content_id: show.slug,
              show_title: show.title,
            }
            sendPbsGtmEvent(eventData);
          } catch (_error) {
            setIsInList(false);
            setMyListShowsData(shows);
          }
        })();
      } else {
        setIsInList(alreadyInList);
      }
    } else if (depIsInList === undefined && video && myListIsLoaded) {
      const videos = myListVideosData ?? [];
      const alreadyInList = videos.some(v => v.slug === video.slug);
      const tmpVideoCid = Cookies.get(TMP_VIDEO_COOKIE);
      if (!alreadyInList && tmpVideoCid && video.cid === tmpVideoCid) {
        setIsInList(true);
        setMyListVideosData([video as VideoClass, ...videos]);
        (async () => {
          try {
            await addFavoriteVideo(video.cid);
            Cookies.remove(TMP_VIDEO_COOKIE);
            const eventData: EventData = {
              ...baseEventData,
              franchise_title: video.franchise?.title,
              object_action_behavior: "adds video to list after sign in",
              object_name: "my list - add video to list",
              pbs_content_id: video.slug,
              show_title: video.show?.title,
              video_title: video.title,
              video_tp_media_id: video.legacy_tp_media_id,
              video_type: video.video_type,
            }
            sendPbsGtmEvent(eventData);
          } catch (_error) {
            setIsInList(false);
            setMyListVideosData(videos);
          }
        })();
      } else {
        setIsInList(alreadyInList);
      }
    }
  }, [
    depIsInList,
    myListIsLoaded,
    myListShowsData,
    myListVideosData,
    setMyListShowsData,
    setMyListVideosData,
    show,
    video,
  ])

  const handleRemoveFavoriteShow = async (show: ShowRowContent | ShowDetails) => {
    // 1. Update local state so this is not in the list
    setIsInList(false)
    // 2. Update MyListShowsData in session storage to delete this show
    const updatedShowsList = myListShowsData?.filter(s => s.slug !== show.slug);
    setMyListShowsData(updatedShowsList ?? null);
    // 3. Side Effect: Update CS / Profile to remove the show from user's profile
    await removeFavoriteShow(show.cid)
    // 4. Side Effect: Update the profile object in local storage
    // @TODO: is this really still necessary? We don't use this data for our app now.
    const newProfile = { ...profile } as ProfileData
    const content = newProfile.personal_data.favorite_shows.content as PersonalFavoriteShowsContent[]
    const filteredContent = content.filter(x => x.id !== show.slug)
    newProfile.personal_data.favorite_shows.content = filteredContent;
    setProfile(newProfile)
    // @TODO The BI team *may* ask us to remove feature tracking from
    // my list _removal_ events, if they are too noisy.
    const eventData: EventData = {
      ...baseEventData,
      franchise_title: show.franchise?.title,
      object_action_behavior: "removes show from list",
      object_name: "my list - remove show from list",
      pbs_content_id: show.slug,
      show_title: show.title,
    }
    sendPbsGtmEvent(eventData);
  }

  const handleAddFavoriteShow = async (show: ShowRowContent | ShowDetails) => {
    // 1. Update local state so that this is in the list
    setIsInList(true)
    // 2. Update MyListShowsData in session storage to add this show
    // this inserts the show at the beginning of my list
    setMyListShowsData([show, ...myListShowsData || []])
    // 3. Side Effect: Update CS / Profile to add the show to user's profile
    await addFavoriteShow(show.cid)
    // 4. Side Effect: Update the profile object in local storage
    // @TODO: is this really still necessary? We don't use this data for our app now.
    const newProfile = { ...profile } as ProfileData
    newProfile.personal_data.favorite_shows.content.push({ id: show.slug })
    setProfile(newProfile)

    const eventData: EventData = {
      ...baseEventData,
      franchise_title: show.franchise?.title,
      object_action_behavior: "adds show to list",
      object_name: "my list - add show to list",
      pbs_content_id: show.slug,
      show_title: show.title,
    }
    sendPbsGtmEvent(eventData);
  }

  const handleRemoveFavoriteVideo = async (video: VideoClass) => {
    // 1. Update local state so this is not in the list
    setIsInList(false)
    // 2. Update myListVideosData in session storage to delete this video
    const updatedVideosList = myListVideosData?.filter(v => v.slug !== video.slug);
    setMyListVideosData(updatedVideosList ?? null);
    // 3. Side Effect: Update CS / Profile to remove the video from user's profile
    await removeFavoriteVideo(video.cid)
    // 4. Side Effect: Update the profile object in local storage
    // @TODO: is this really still necessary? We don't use this data for our app now.
    const newProfile = { ...profile } as ProfileData
    const content = newProfile.personal_data.favorite_videos.content as PersonalFavoriteVideosContent[]
    const filteredContent = content.filter(x => x.id !== video.slug)
    newProfile.personal_data.favorite_videos.content = filteredContent;
    setProfile(newProfile)
    // @TODO The BI team *may* ask us to remove feature tracking from
    // my list _removal_ events, if they are too noisy.
    const eventData: EventData = {
      ...baseEventData,
      franchise_title: video.franchise?.title,
      object_action_behavior: "removes video from list",
      object_name: "my list - remove video from list",
      pbs_content_id: video.slug,
      show_title: video.show?.title,
      video_title: video.title,
      video_tp_media_id: video.legacy_tp_media_id,
      video_type: video.video_type,
    }
    sendPbsGtmEvent(eventData);
  }

  const handleAddFavoriteVideo = async(video: VideoClass) => {
    // 1. Update local state so that this is in the list
    setIsInList(true)
    // 2. Update myListVideosData in session storage to add this video
    // this inserts the video at the beginning of my list
    setMyListVideosData([video, ...myListVideosData || []])
    // 3. Side Effect: Update CS / Profile to add the video to user's profile
    await addFavoriteVideo(video.cid)
    // 4. Side Effect: Update the profile object in local storage
    // @TODO: is this really still necessary? We don't use this data for our app now.
    const newProfile = { ...profile } as ProfileData
    newProfile.personal_data.favorite_videos.content.push({ id: video.slug })
    setProfile(newProfile)

    const eventData: EventData = {
      ...baseEventData,
      franchise_title: video.franchise?.title,
      object_action_behavior: "adds video to list",
      object_name: "my list - add video to list",
      pbs_content_id: video.slug,
      show_title: video.show?.title,
      video_title: video.title,
      video_tp_media_id: video.legacy_tp_media_id,
      video_type: video.video_type,
    }
    sendPbsGtmEvent(eventData);
  }

  const handleClick = async () => {
    switch(true) {
      case show !== undefined && isInList:
        await handleRemoveFavoriteShow(show)
        break;
      case show !== undefined && !isInList:
        await handleAddFavoriteShow(show as ShowRowContent)
        break;
      case video !== undefined && isInList:
        await handleRemoveFavoriteVideo(video as VideoClass)
        break;
      case video !== undefined && !isInList:
        await handleAddFavoriteVideo(video as VideoClass)
        break;
      default:
        break;
    }
  }

  let content = (<></>);
  let buttonClass = styles.my_list_button;

  switch(true) {
    case style === 'iconOnly' && isInList:
      buttonClass = styles.my_list_button__icon_only;
      content = (
        <>
          <CheckIcon />
          <span className="visuallyhidden">Remove from My List</span>
        </>
      )
      break;
    case style === 'iconOnly' && !isInList:
      buttonClass = styles.my_list_button__icon_only;
      content = (
        <>
          <PlusIcon />
          <span className="visuallyhidden">Add to My List</span>
        </>
      )
      break;
    case style === 'kabobMenu' && isInList:
      buttonClass = styles.my_list_button__kabob_menu;
      content = (
        <>
          <MinusIcon />
          <span>Remove from My List</span>
        </>
      )
      break;
    case style === 'kabobMenu' && !isInList:
      buttonClass = styles.my_list_button__kabob_menu;
      content = (
        <>
          <PlusIcon />
          <span>Add to My List</span>
        </>
      )
      break;
  }

  if (!myListIsLoaded) {
    return null;
  }

  return (
    <button className={buttonClass} onClick={handleClick}>
      {content}
    </button>
  )
}

export default SignedInMyListButton
